import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Checkbox, Input, Button, Typography, Spin } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getPaymentDetails } from '../../actions/Actions';
import { useAuth0 } from '@auth0/auth0-react';

const { Title, Text } = Typography;
const { TextArea } = Input;

const DraftingInstructions = () => {
  const { transactionId } = useParams();
  const { isAuthenticated } = useAuth0();
  const dispatch = useDispatch();
  const paymentDetails = useSelector((state) => state.paymentsDraft?.paymentDetails);
  const isLoading = useSelector((state) => state.paymentsDraft?.paymentDetailsLoading);
  
  const [formData, setFormData] = useState({
    mailingAddress: '',
    contactName: '',
    phone: '',
    deliveryMethod: {
      mail: false,
      pickup: false,
      fedex: false
    }
  });

  useEffect(() => {
    // Fetch payment details when component mounts or transactionId changes
    if (transactionId && isAuthenticated) {
      console.log('Fetching payment details for transaction:', transactionId);
      dispatch(getPaymentDetails(transactionId));
    }
  }, [transactionId, dispatch]);

  const handleDeliveryMethodChange = (method, checked) => {
    setFormData(prev => ({
      ...prev,
      deliveryMethod: {
        ...prev.deliveryMethod,
        [method]: checked
      }
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = () => {
    const submitData = {
      transactionId,
      ...formData
    };
    console.log('Form data:', submitData);
    // Handle form submission - call API to save the data
    // dispatch(savePaymentRequest(submitData));
  };

  const handlePrint = () => {
    window.print();
  };

  // Show loading spinner while fetching data
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <Spin size="large" tip="Loading payment details..." />
      </div>
    );
  }

  // Show error message if no data
  if (!paymentDetails) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <Text type="danger">Unable to load payment details. Please try again.</Text>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
            
            /* Hide common layout elements */
            .ant-layout-sider,
            .ant-layout-header,
            .ant-menu,
            nav,
            header,
            footer,
            .sidebar,
            .navbar,
            .nav-bar,
            .header,
            .footer,
            .menu,
            aside {
              display: none !important;
            }
            
            /* Make main content full width */
            .ant-layout-content,
            .ant-layout,
            main {
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
            }
            
            /* Reset page margins for printing */
            @page {
              margin: 0.5cm;
              size: A4 portrait;
            }
            
            body {
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
              margin: 0 !important;
              padding: 0 !important;
              font-size: 9px !important;
            }
            
            /* Ensure the drafting page takes full width */
            .responsive-container {
              max-width: 100% !important;
              padding: 10px !important;
            }
            
            /* Scale down logo */
            .responsive-logo {
              max-width: 400px !important;
              height: auto !important;
            }
            
            /* Reduce title size */
            .responsive-title,
            .responsive-title h2 {
              font-size: 18px !important;
              margin: 5px 0 !important;
            }
            
            /* Reduce contact info */
            .responsive-contact-info {
              font-size: 8px !important;
              margin-bottom: 10px !important;
            }
            
            /* Maintain grid layout */
            .ant-row {
              display: flex !important;
              flex-wrap: wrap !important;
            }
            
            .ant-col {
              display: block !important;
            }
            
            /* Force 2-column layout for main content */
            .responsive-grid .ant-col-12 {
              width: 50% !important;
              flex: 0 0 50% !important;
              max-width: 50% !important;
            }
            
            /* Reduce grid spacing */
            .responsive-grid {
              border: 2px solid #000 !important;
              padding: 5px !important;
              margin-bottom: 8px !important;
            }
            
            /* Reduce row spacing */
            .responsive-row {
              font-size: 9px !important;
              margin-bottom: 4px !important;
              line-height: 1.3 !important;
            }
            
            .responsive-label {
              font-size: 9px !important;
            }
            
            .responsive-value {
              font-size: 9px !important;
            }
            
            /* Reduce yellow warning box */
            div[style*="background-color: rgb(255, 255, 0)"],
            div[style*="backgroundColor: #FFFF00"] {
              background-color: #FFFF00 !important;
              padding: 5px !important;
              margin: 5px 0 !important;
              font-size: 9px !important;
            }
            
            /* Reduce white slip names section */
            .responsive-container > div[style*="margin"] {
              margin: 5px 0 !important;
              font-size: 9px !important;
            }
            
            /* Force 3-column layout for cards */
            .responsive-card {
              width: 33.333% !important;
              flex: 0 0 33.333% !important;
              max-width: 33.333% !important;
            }
            
            /* Reduce card sizes */
            .ant-card {
              break-inside: avoid;
              page-break-inside: avoid;
              height: auto !important;
              min-height: 160px !important;
              font-size: 8px !important;
            }
            
            .ant-card-head {
              padding: 5px 10px !important;
              min-height: auto !important;
            }
            
            .ant-card-head-title {
              font-size: 9px !important;
              font-weight: bold !important;
              padding: 0 !important;
            }
            
            .ant-card-body {
              padding: 8px !important;
              font-size: 8px !important;
            }
            
            .ant-card-body .ant-typography {
              font-size: 8px !important;
            }
            
            /* Reduce textarea size */
            .ant-input,
            textarea {
              font-size: 8px !important;
              padding: 4px !important;
            }
            
            /* Reduce checkbox size */
            .ant-checkbox-wrapper {
              font-size: 8px !important;
              margin-bottom: 3px !important;
            }
            
            /* Keep borders and styling */
            .responsive-grid {
              border: 2px solid #000 !important;
            }
            
            /* Prevent page breaks in important sections */
            .responsive-row,
            .ant-card,
            .responsive-grid {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            
            /* Reduce footer size */
            .responsive-footer {
              font-size: 7px !important;
              line-height: 1.2 !important;
              margin-top: 10px !important;
            }
            
            .responsive-footer strong {
              font-size: 7px !important;
            }
            
            /* Reduce yellow footer box */
            .responsive-footer div[style*="backgroundColor"] {
              padding: 3px !important;
              margin: 3px 0 !important;
              font-size: 7px !important;
            }
          }
          
          @media screen and (max-width: 768px) {
            .responsive-container {
              padding: 10px !important;
            }
            
            .responsive-logo {
              max-width: 100% !important;
              height: auto !important;
            }
            
            .responsive-title {
              font-size: 20px !important;
            }
            
            .responsive-contact-info {
              font-size: 10px !important;
              line-height: 1.4 !important;
            }
            
            .responsive-grid {
              border: 1px solid #000 !important;
              padding: 5px !important;
            }
            
            .responsive-row {
              font-size: 11px !important;
              margin-bottom: 8px !important;
            }
            
            .responsive-label {
              font-weight: bold !important;
              display: block !important;
              margin-bottom: 2px !important;
            }
            
            .responsive-value {
              display: block !important;
            }
            
            .responsive-card .ant-card {
              font-size: 11px !important;
            }
            
            .responsive-card .ant-card-head-title {
              font-size: 12px !important;
              padding: 8px 0 !important;
            }
            
            .responsive-card .ant-card-body {
              padding: 10px !important;
            }
            
            .responsive-footer {
              font-size: 9px !important;
              line-height: 1.2 !important;
            }
            
            .responsive-button {
              width: 100% !important;
              margin-bottom: 10px !important;
            }
          }
          
          @media screen and (max-width: 576px) {
            .responsive-contact-info {
              font-size: 9px !important;
            }
            
            .responsive-card {
              margin-bottom: 10px !important;
            }
          }
        `}
      </style>
      <div className="responsive-container" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', backgroundColor: '#fff' }}>
      {/* Header Logo */}
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <img 
          src={paymentDetails.logoUrl} 
          alt="Lender Logo" 
          className="responsive-logo"
          style={{ maxWidth: '600px', height: 'auto' }}
        />
      </div>

      {/* Print Button - Top Right */}
      <div className="no-print" style={{ textAlign: 'right', marginBottom: '10px' }}>
        <Button 
          type="default" 
          icon={<PrinterOutlined />} 
          onClick={handlePrint}
          size="large"
          className="responsive-button"
        >
          Print / Save as PDF
        </Button>
      </div>

      {/* Title */}
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <Title level={2} className="responsive-title" style={{ margin: 0, fontWeight: 'bold' }}>DRAFTING INSTRUCTIONS</Title>
      </div>

      {/* Contact Information */}
      <div className="responsive-contact-info" style={{ textAlign: 'center', marginBottom: '20px', fontSize: '12px' }}>
        <Text>
          <strong>EMAIL:</strong> {paymentDetails.contactEmail || 'CUTXDRAFTING@CUTX.ORG'} | 
          <strong> FOR DEALER PAYMENT ONLY:</strong> {paymentDetails.dealerPaymentPhone || '972-301-1986'} | 
          <strong> FOR NEW DRAFTING ONLY:</strong> {paymentDetails.newDraftingPhone || '469-847-5831'} | 
          <strong> BUSINESS HOURS:</strong> {paymentDetails.businessHours || '9AM - 5PM'}
        </Text>
      </div>

      {/* Main Content */}
      <Row gutter={[16, 16]} className="responsive-grid" style={{ border: '2px solid #000', padding: '10px' }}>
        {/* First Row */}
        <Col xs={24} sm={12} className="ant-col-12">
          <Row className="responsive-row">
            <Col span={24}>
              <Text strong className="responsive-label">DATE:</Text>
              <Text className="responsive-value">{paymentDetails.date}</Text>
            </Col>
          </Row>
        </Col>
        <Col xs={24} sm={12} className="ant-col-12">
          <Row className="responsive-row">
            <Col span={24}>
              <Text strong className="responsive-label">LIEN DATE:</Text>
              <Text className="responsive-value">{paymentDetails.lienDate}</Text>
            </Col>
          </Row>
        </Col>

        {/* Second Row */}
        <Col xs={24} sm={12} className="ant-col-12">
          <Row className="responsive-row">
            <Col span={24}>
              <Text strong className="responsive-label">TO:</Text>
              <Text className="responsive-value">{paymentDetails.to}</Text>
            </Col>
          </Row>
        </Col>
        <Col xs={24} sm={12} className="ant-col-12">
          <Row className="responsive-row">
            <Col span={24}>
              <Text strong className="responsive-label">AMOUNT:</Text>
              <Text className="responsive-value">{paymentDetails.amount}</Text>
            </Col>
          </Row>
        </Col>

        {/* Third Row */}
        <Col xs={24} sm={12} className="ant-col-12">
          <Row className="responsive-row">
            <Col span={24}>
              <Text strong className="responsive-label">VALIDATION CODE:</Text>
              <Text className="responsive-value">
                {paymentDetails.validationCode} {paymentDetails.validationSuffix}
              </Text>
            </Col>
          </Row>
        </Col>
        <Col xs={24} sm={12} className="ant-col-12">
          <Row className="responsive-row">
            <Col span={24}>
              <Text strong className="responsive-label">YEAR:</Text>
              <Text className="responsive-value">{paymentDetails.year}</Text>
            </Col>
          </Row>
        </Col>

        {/* Fourth Row */}
        <Col xs={24} sm={12} className="ant-col-12">
          <Row className="responsive-row">
            <Col span={24}>
              <Text strong className="responsive-label">FAX:</Text>
              <Text className="responsive-value">{paymentDetails.fax}</Text>
            </Col>
          </Row>
        </Col>
        <Col xs={24} sm={12} className="ant-col-12">
          <Row className="responsive-row">
            <Col span={24}>
              <Text strong className="responsive-label">MAKE:</Text>
              <Text className="responsive-value">{paymentDetails.make}</Text>
            </Col>
          </Row>
        </Col>

        {/* Fifth Row */}
        <Col xs={24} sm={12} className="ant-col-12">
          <Row className="responsive-row">
            <Col span={24}>
              <Text strong className="responsive-label">EMAIL:</Text>
              <Text className="responsive-value">{paymentDetails.email}</Text>
            </Col>
          </Row>
        </Col>
        <Col xs={24} sm={12} className="ant-col-12">
          <Row className="responsive-row">
            <Col span={24}>
              <Text strong className="responsive-label">MODEL:</Text>
              <Text className="responsive-value">{paymentDetails.model}</Text>
            </Col>
          </Row>
        </Col>

        {/* Sixth Row */}
        <Col xs={24} sm={12} className="ant-col-12">
          <Row className="responsive-row">
            <Col span={24}>
              <Text strong className="responsive-label">FROM:</Text>
              <Text className="responsive-value">{paymentDetails.from}</Text>
            </Col>
          </Row>
        </Col>
        <Col xs={24} sm={12} className="ant-col-12">
          <Row className="responsive-row">
            <Col span={24}>
              <Text strong className="responsive-label">VIN:</Text>
              <Text className="responsive-value">{paymentDetails.vin}</Text>
            </Col>
          </Row>
        </Col>

        {/* Seventh Row */}
        <Col xs={24} sm={12} className="ant-col-12">
          <Row className="responsive-row">
            <Col span={24}>
              <Text strong className="responsive-label">Agent:</Text>
              <Text className="responsive-value">{paymentDetails.agent}</Text>
            </Col>
          </Row>
        </Col>
        <Col xs={24} sm={12} className="ant-col-12">
          <Row className="responsive-row">
            <Col span={24}>
              <Text strong className="responsive-label">TRADE IN VIN:</Text>
              <Text className="responsive-value">{paymentDetails.tradeInVin}</Text>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* E-Title Warning */}
      {paymentDetails.eTitle && (
        <div style={{ 
          backgroundColor: '#FFFF00', 
          padding: '8px', 
          margin: '10px 0', 
          textAlign: 'center',
          border: '1px solid #000',
          fontWeight: 'bold'
        }}>
          <Text>{paymentDetails.eTitle}</Text>
        </div>
      )}

      {/* Names on White Slip */}
      {paymentDetails.whiteSlipNames && (
        <>
          <Row style={{ margin: '10px 0' }}>
            <Col span={24}>
              <Text strong>NAMES ON WHITESLIP: </Text>
              <Text>{paymentDetails.whiteSlipNames.name1}</Text>
            </Col>
          </Row>
          {paymentDetails.whiteSlipNames.name2 && (
            <Row style={{ margin: '5px 0' }}>
              <Col span={24}>
                <Text strong>AND: </Text>
                <Text>{paymentDetails.whiteSlipNames.name2}</Text>
              </Col>
            </Row>
          )}
        </>
      )}

      {/* Bottom Section */}
      <Row gutter={[16, 16]} style={{ marginTop: '20px' }}>
        {/* Lien Holder and Mailing Address */}
        <Col xs={24} md={8} className="responsive-card">
          <Card title="LIEN HOLDER AND MAILING ADDRESS:" size="small" style={{ minHeight: '250px' }}>
            <div style={{ textAlign: 'center' }}>
              <Text strong>{paymentDetails.lienHolder?.name}</Text>
              <br />
              <Text style={{ whiteSpace: 'pre-line' }}>
                {paymentDetails.lienHolder?.address}
              </Text>
            </div>
            <div style={{ marginTop: '20px' }}>
              <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                Dealer Mailing Address:
              </Text>
              <TextArea
                placeholder="Enter mailing address if different..."
                value={formData.mailingAddress}
                onChange={(e) => handleInputChange('mailingAddress', e.target.value)}
                rows={3}
              />
            </div>
          </Card>
        </Col>

        {/* Dealer Instructions */}
        <Col span={8}>
          <Card title="DEALER INSTRUCTIONS:" size="small" style={{ height: '250px' }}>
            <div style={{ fontSize: '12px' }}>
              <Text>PLEASE EMAIL COPY OF WHITE SLIP</Text>
              <br />
              <Text>AND DRAFTING INSTRUCTIONS TO</Text>
              <br />
              <Text strong style={{ color: '#0066CC' }}>
                {paymentDetails.dealerInstructions?.email}
              </Text>
              <br /><br />
              <Text strong>FLOOR PLAN BANKING INSTRUCTIONS:</Text>
              <br />
              <Text style={{ whiteSpace: 'pre-line', fontSize: '11px' }}>
                {paymentDetails.dealerInstructions?.floorPlan}
              </Text>
            </div>
          </Card>
        </Col>

        {/* Contact and Delivery */}
        <Col xs={24} md={8} className="responsive-card">
          <Card title="CONTACT NAME:" size="small" style={{ minHeight: '250px' }}>
            <Text strong>HOW WOULD YOU LIKE TO RECEIVE YOUR CHECK?</Text>
            <div style={{ marginTop: '10px' }}>
              <div style={{ marginBottom: '5px' }}>
                <Checkbox
                  checked={formData.deliveryMethod.mail}
                  onChange={(e) => handleDeliveryMethodChange('mail', e.target.checked)}
                >
                  MAIL
                </Checkbox>
              </div>
              <div style={{ marginBottom: '5px' }}>
                <Checkbox
                  checked={formData.deliveryMethod.pickup}
                  onChange={(e) => handleDeliveryMethodChange('pickup', e.target.checked)}
                >
                  PICK UP AT CUTX - <Text style={{ fontSize: '10px' }}>
                    {paymentDetails.pickupAddress}
                  </Text>
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={formData.deliveryMethod.fedex}
                  onChange={(e) => handleDeliveryMethodChange('fedex', e.target.checked)}
                >
                  FEDEX <Text style={{ fontSize: '10px' }}>
                    {paymentDetails.fedexNote}
                  </Text>
                </Checkbox>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Footer Warnings */}
      <div className="responsive-footer" style={{ marginTop: '20px', fontSize: '11px', lineHeight: '1.3' }}>
        <Text>
          <strong>DRAFT WILL NOT BE HONORED IF TITLE OR TAX RECEIPT REFLECTS BONDED, SALVAGED, REBUILT, MANUFACTURER BUYBACK, RECONDITIONED, OR MILES UNKNOWN</strong>
        </Text>
        <br />
        <div style={{ backgroundColor: '#FFFF00', padding: '5px', margin: '5px 0', display: 'inline-block' }}>
          <Text strong>
            "PAPER AND WHITESLIP MAY BE BROUGHT TO ANY CUTX LOCATION, CHECKS ARE ONLY ISSUED AT BETHANY LOCATION AFTER 2 DAY BUSINESS HOLD."
          </Text>
        </div>
        <br />
        <Text style={{ fontSize: '10px' }}>
          IF FOR ANY REASON, THE TITLE ISSUED DOESN'T HAVE CREDIT UNION OF TEXAS AS LIENHOLDER, THE DRAFTING INSTRUCTIONS WILL EXPIRE IN 60 DAYS 
          FROM DATE OF ISSUANCE, DRAFT WILL BE VOIDED IF ANY CHANGES ARE MADE OR BUYER DOES NOT COMPLY WITH LENDING REQUIREMENTS SET FORTH BY 
          CREDIT UNION OF TEXAS, TO INCLUDE EXECUTION OF DOCUMENTS AND DOWN PAYMENT AS APPLICABLE.
        </Text>
      </div>

      {/* Submit Button */}
      <div className="no-print" style={{ textAlign: 'center', marginTop: '30px' }}>
        <Button type="primary" size="large" onClick={handleSubmit} className="responsive-button">
          Submit Drafting Instructions
        </Button>
      </div>
    </div>
    </>
  );
};

export default DraftingInstructions;